import { useEffect, useMemo, useState } from "react";
import { Controller, SubmitHandler, useForm, useWatch } from "react-hook-form";
import { useNavigate } from "react-router";
import * as z from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { toast } from "react-toastify";
import { useSpecListsForSpecificMask } from "@/hooks/useSpecLists";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Form, FormControl, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Combobox } from "@/components/ui/combobox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useCreateTestLabel, useTestLabelsByMask } from "@/hooks/useTestLabels";

import { Mask } from "@/types/mask";
import { RawTestLabel } from "@/types/testLabel";
import { useMaskStructures } from "@/hooks/useMasks";
import { useMeasurementCapabilities } from "@/hooks/useMeasurementCapabilities";

import { Switch } from "../../ui/switch";
import { SpecsTableWrapper } from "./components/SpecsSelectWrapper";
import DeviceSelectTable from "@/components/shared/DeviceSelectTable";

const formSchema = z.object({
  maskId: z.string({
    message: "Field is required.",
  }),
  name: z.string().min(1, {
    message: "Field is required.",
  }),
  specListId: z.string().nullable(),
  measurementCapabilityId: z.string({
    message: "Field is required",
  }),
  description: z.string().min(1, {
    message: "Field is required.",
  }),
});

type FormSchema = z.infer<typeof formSchema>;

type CreateTestLabelDialogProps = {
  masks: Mask[];
  navigateAfterCreation?: boolean;
  onOpenChange: (open: boolean) => void;
  preSelectedMaskId?: string;
  refetchData?: () => void;
};

function CreateTestLabelDialog({
  masks,
  navigateAfterCreation = false,
  onOpenChange,
  preSelectedMaskId,
  refetchData,
}: CreateTestLabelDialogProps) {
  const navigate = useNavigate();
  const { trigger: createTestLabel, isMutating: isCreatingTestLabel } = useCreateTestLabel();

  const [customMode, setCustomMode] = useState<boolean>(false);
  const [specSelectionState, setSpecSelectionState] = useState<Record<string, boolean>>({});
  const [deviceSelectionState, setDeviceSelectionState] = useState<Record<string, boolean>>({});

  // Find the mask that matches the query parameter
  const preselectedMask = useMemo(() => {
    if (preSelectedMaskId && masks.length > 0) {
      return masks.find((mask) => mask.id === preSelectedMaskId);
    }
    return undefined;
  }, [masks, preSelectedMaskId]);

  const form = useForm<FormSchema>({
    defaultValues: {
      description: "",
      maskId: preselectedMask?.id || undefined,
      name: "",
      specListId: null,
      measurementCapabilityId: undefined,
    },
    resolver: zodResolver(formSchema),
    mode: "all",
  });

  const {
    control,
    register,
    handleSubmit,
    formState: { errors },
    resetField,
    setValue,
  } = form;

  const { maskId, specListId, measurementCapabilityId } = useWatch({
    control,
  });

  const { data: specLists, isLoading: isSpecListsForMaskLoading } =
    useSpecListsForSpecificMask(maskId);

  const { data: testLabelsForMask = [] } = useTestLabelsByMask(maskId);

  const { data: maskWithStructures, isLoading: isMaskStructuresLoading } =
    useMaskStructures(maskId);

  const { data: measurementCapabilities, isLoading: isMeasurementCapabilitiesLoading } =
    useMeasurementCapabilities();

  const onChangeMaskReset = () => {
    resetField("specListId");
  };

  const onChangeComponentType = () => {
    setSpecSelectionState({});
  };

  const { customStructures, hasStandardStructures, hasCustomStructures, canToggleMode } =
    useMemo(() => {
      const standard = maskWithStructures?.structures.filter((s) => !s.isCustom) ?? [];
      const custom = maskWithStructures?.structures.filter((s) => s.isCustom) ?? [];
      const hasStandard = standard.length > 0;
      const hasCustom = custom.length > 0;

      return {
        standardStructures: standard,
        customStructures: custom,
        hasStandardStructures: hasStandard,
        hasCustomStructures: hasCustom,
        canToggleMode: hasStandard && hasCustom,
      };
    }, [maskWithStructures?.structures]);

  const specRowSelection = Object.keys(specSelectionState).filter((key) => specSelectionState[key]);
  const deviceRowSelection = Object.keys(deviceSelectionState).filter(
    (key) => deviceSelectionState[key],
  );

  const supportedRecipes = useMemo(() => {
    const measurementCapability = measurementCapabilities.find(
      (c) => c.id === measurementCapabilityId,
    );

    if (!measurementCapability) return [];

    return measurementCapability.recipes;
  }, [measurementCapabilities, measurementCapabilityId]);

  const onSubmit: SubmitHandler<FormSchema> = async (data) => {
    const conflict = testLabelsForMask.some(
      (tl) => tl.name.toLowerCase() === data.name.trim().toLowerCase(),
    );
    if (conflict) {
      form.setError("name", {
        message: "A test label with this name already exists for this mask. Try a different name.",
      });
      return;
    }
    try {
      const testLabel = {
        mask_id: data.maskId,
        name: data.name,
        description: data.description,
        spec_list_id: data.specListId,
        measurement_capability_id: data.measurementCapabilityId,
        linked_spec_ids: !customMode ? specRowSelection : [],
        linked_structure_ids: customMode ? deviceRowSelection : [],
      };
      await createTestLabel(testLabel, {
        onError: (err) => toast.error(`Error: ${err.message}`),
        onSuccess: (testLabel: RawTestLabel) => {
          refetchData?.();
          toast.success(`Successfully created Test Label: "${data.name}"`);
          onOpenChange(false);
          if (navigateAfterCreation) {
            navigate(`/test-labels/${testLabel.id}`);
          }
        },
      });
    } catch (err) {
      console.error(err);
    }
  };

  const canSubmit = customMode ? deviceRowSelection.length > 0 : specRowSelection.length > 0;

  // Effect to set the mask value when preselectedMask is available
  useEffect(() => {
    if (preselectedMask && !maskId) {
      setValue("maskId", preselectedMask.id);
    }
  }, [preselectedMask, maskId, setValue]);

  useEffect(() => {
    if (hasCustomStructures && !hasStandardStructures) {
      setCustomMode(true);
    }
    if (customStructures.length === 1 && deviceRowSelection.length === 0) {
      // If only one custom structure exists, select it by default
      setDeviceSelectionState({ [customStructures[0].id]: true });
    }
  }, [customStructures, hasCustomStructures, hasStandardStructures, deviceRowSelection.length]);

  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent className="md:max-w-[80%] lg:w-[90%] lg:max-w-350 mx-auto">
        <DialogHeader className="shrink-0">
          <DialogTitle>Create New Test Label</DialogTitle>
        </DialogHeader>
        <DialogDescription>Create a new Test Label for the current mask</DialogDescription>
        <Form {...form}>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg">General</CardTitle>
                  {canToggleMode && (
                    <div className="flex items-center gap-2 border rounded-md px-3 py-1.5">
                      <span className="text-sm text-muted-foreground">Use custom designs</span>
                      <Switch checked={customMode} onCheckedChange={setCustomMode} />
                    </div>
                  )}
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <FormItem>
                  <FormLabel htmlFor="testLabelName">
                    Name <span className="text-red-500">*</span>
                  </FormLabel>
                  <FormControl>
                    <Input
                      id="testLabelName"
                      placeholder="Enter a Test Label name"
                      {...register("name")}
                    />
                  </FormControl>
                  {errors.name && <FormMessage>{errors.name.message}</FormMessage>}
                </FormItem>

                <FormItem>
                  <FormLabel htmlFor="description">
                    Description <span className="text-red-500">*</span>
                  </FormLabel>
                  <FormControl>
                    <Input
                      id="description"
                      placeholder="Enter a description for the Test Label"
                      {...register("description")}
                    />
                  </FormControl>
                  {errors.description && <FormMessage>{errors.description.message}</FormMessage>}
                </FormItem>

                <FormItem className={preSelectedMaskId ? "hidden" : ""}>
                  <FormLabel htmlFor="maskId">
                    Mask
                    <span className="text-red-500">*</span>
                  </FormLabel>
                  <FormControl>
                    <Controller
                      control={control}
                      name="maskId"
                      render={({ field }) => (
                        <Combobox
                          fullWidth
                          modal
                          onChange={(value) => {
                            field.onChange(value);
                            onChangeMaskReset();
                          }}
                          options={masks.map((mask) => ({
                            key: mask.id,
                            label: mask.name,
                            value: mask.id,
                          }))}
                          placeholder="Select a mask"
                          searchPlaceholder="Search masks..."
                          value={field.value}
                        />
                      )}
                    />
                  </FormControl>
                  {errors.maskId && <FormMessage>{errors.maskId.message}</FormMessage>}
                </FormItem>

                {!customMode && (
                  <FormItem>
                    <FormLabel htmlFor="specListId">
                      Spec List <span className="text-red-500">*</span>
                    </FormLabel>
                    <FormControl>
                      <Controller
                        control={control}
                        name="specListId"
                        render={({ field }) => (
                          <Combobox
                            disabled={specLists.length === 0}
                            fullWidth
                            modal
                            onChange={field.onChange}
                            options={specLists.map((specList) => ({
                              key: specList.id,
                              label: specList.name,
                              value: specList.id,
                            }))}
                            placeholder={
                              isSpecListsForMaskLoading
                                ? "Loading spec lists..."
                                : !maskId
                                  ? "Select a mask first"
                                  : specLists.length === 0
                                    ? "No spec lists available for this mask"
                                    : "Select a spec list"
                            }
                            searchPlaceholder="Search spec list..."
                            value={field.value}
                          />
                        )}
                      />
                    </FormControl>
                    {errors.specListId && <FormMessage>{errors.specListId.message}</FormMessage>}
                  </FormItem>
                )}

                <FormItem>
                  <FormLabel htmlFor="measurementCapabilityId">
                    Measurement Capability <span className="text-red-500">*</span>
                  </FormLabel>
                  <FormControl>
                    <Controller
                      control={control}
                      name="measurementCapabilityId"
                      render={({ field }) => (
                        <Combobox
                          disabled={!maskId || (!specListId && !customMode)}
                          fullWidth
                          modal
                          onChange={(value) => {
                            field.onChange(value);
                            onChangeComponentType();
                          }}
                          options={measurementCapabilities.map((capability) => ({
                            key: capability.id,
                            label: capability.name,
                            value: capability.id,
                          }))}
                          placeholder={
                            isMeasurementCapabilitiesLoading
                              ? "Loading measurement capabilities..."
                              : measurementCapabilities.length === 0
                                ? "No measurement capabilities found"
                                : "Select a measurement capability"
                          }
                          searchPlaceholder="Search measurement capability..."
                          value={field.value}
                        />
                      )}
                    />
                  </FormControl>
                  {errors.measurementCapabilityId && (
                    <FormMessage>{errors.measurementCapabilityId.message}</FormMessage>
                  )}
                </FormItem>
              </CardContent>
            </Card>

            {maskId && specListId && measurementCapabilityId && !customMode ? (
              <SpecsTableWrapper
                maskWithStructures={{
                  ...maskWithStructures,
                  structures: maskWithStructures?.structures.filter((s) => !s.isCustom) ?? [],
                }}
                onRowSelectionChange={setSpecSelectionState}
                rowSelectionState={specSelectionState}
                specListId={specListId}
                specLists={specLists}
                isLoading={isSpecListsForMaskLoading || isMaskStructuresLoading}
                supportedRecipes={supportedRecipes}
              />
            ) : null}
            {maskId && measurementCapabilityId && customMode ? (
              <DeviceSelectTable
                deviceData={customStructures}
                onRowSelectionChange={setDeviceSelectionState}
                rowSelectionState={deviceSelectionState}
              />
            ) : null}

            <DialogFooter>
              <Button onClick={() => onOpenChange(false)} type="button" variant="outline">
                Cancel
              </Button>
              <Button type="submit" disabled={isCreatingTestLabel || !canSubmit}>
                Create
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}

export default CreateTestLabelDialog;
